/**
 * UI metadata for the platform's `agent_config` keys — labels,
 * descriptions, the operator-facing section taxonomy, and the editor
 * kind a row uses on `/settings`.
 *
 * Lives in `@prospect/shared` (not next to `AGENT_CONFIG_DEFAULTS` in
 * `@prospect/google`) because the SPA — admin-ui's `/settings` panel
 * AND the JobDetail "Config used" snapshot panel — needs to read it
 * client-side, and `@prospect/google` pulls in `googleapis`, which is
 * Node-only. The pattern matches `agent-config-warnings.ts`: pure UI
 * metadata that both the Node-side orchestrator and the browser-side
 * SPA share.
 *
 * `@prospect/google`'s `schemas.ts` re-exports these symbols so
 * existing call-sites (`bootstrap-ops.ts`, `agent-config-write.ts`,
 * the orchestrator's `routes/agent-config.ts`, the bidirectional
 * defaults⇄metadata test) keep working without import-path churn.
 */

/**
 * Section discriminator for the admin-ui `/settings` panel — every key
 * with metadata is grouped under exactly one of these. `"other"` is a
 * UI-only fallback for keys an operator added to the live Sheet that
 * aren't in `AGENT_CONFIG_DEFAULTS`; the metadata map itself only
 * uses the six named sections.
 *
 * `"catalogue"` covers knobs that act on the published
 * `tools_and_services` catalogue rather than on incoming drafts —
 * `stale_threshold_days` is the only resident at present, since its
 * operator-visible behaviour is the per-row `daysUntilStale` badge on
 * `/catalogue`. Distinct from `"curator"` so an operator browsing
 * catalogue freshness doesn't have to scan curator-tuning knobs.
 */
export type AgentConfigSection =
  | "curator"
  | "validator"
  | "catalogue"
  | "runtime"
  | "cost"
  | "notifications"
  | "public-chat";

/**
 * Discriminator for the editor the admin-ui `/settings` panel renders
 * for a given key. Pure UI metadata — the orchestrator's PUT route
 * accepts any string|number for any known key and the agent boot Zod
 * is the loud-fail backstop on type/value mismatches.
 *
 * - `"text"` — plain text input. Default for most keys; preserves
 *   number ↔ string typing by inspecting the row's default.
 * - `"select"` — dropdown over a known enum. Currently
 *   `model_id` and `validator_model_id` (options come from
 *   `KNOWN_MODEL_IDS` in `@prospect/messaging`).
 * - `"email-list"` — comma-separated list of emails, rendered as a
 *   tag-style input. Currently `notify_email`.
 */
export type AgentConfigInputKind = "text" | "select" | "email-list";

/**
 * UI-side metadata for a single `agent_config` key. Co-located with
 * `AGENT_CONFIG_DEFAULTS` (in `@prospect/google`) only via the
 * bidirectional unit test in `packages/google/tests/unit/schemas.test.ts`
 * — that test enforces a metadata entry per default and vice versa, so
 * adding a key here without a default (or vice versa) fails CI.
 */
export interface AgentConfigMetadata {
  /** Title-case label for the row, e.g. `"Quality score threshold"`. */
  readonly label: string;
  /**
   * One- or two-sentence description shown beneath the label. Plain
   * prose; no markdown. Mirrors the rationale comments above each entry
   * in `AGENT_CONFIG_DEFAULTS` so operators reading either source see
   * the same explanation.
   */
  readonly description: string;
  /** Section the key renders under in the `/settings` panel. */
  readonly section: AgentConfigSection;
  /**
   * Editor kind used by the `/settings` Phase 2 row editor. Defaults to
   * `"text"`; overridden to `"select"` for the two `*model_id` keys and
   * `"email-list"` for `notify_email`. The orchestrator's GET response
   * carries this field through to the SPA.
   */
  readonly inputKind: AgentConfigInputKind;
}

/**
 * Per-key UI metadata for every `agent_config` key in
 * `AGENT_CONFIG_DEFAULTS`. Drives both the admin-ui `/settings` panel
 * (`apps/admin-ui/src/routes/Settings.tsx`) and the JobDetail
 * "Config used" snapshot panel
 * (`apps/admin-ui/src/components/AgentConfigSnapshot.tsx`).
 *
 * Section assignment is hand-curated — neither the curator/validator
 * Zod schemas nor the un-prefixed/`validator_`-prefixed naming
 * convention map cleanly to the operator-facing taxonomy ("cost",
 * "notifications"). The bidirectional coverage test catches the only
 * realistic drift mode: adding a key to `AGENT_CONFIG_DEFAULTS`
 * without giving it a metadata entry.
 *
 * Notes on a few placement choices:
 * - `notify_email` lives in **Notifications** (single home) — its
 *   description calls out that it's also the cost-alert recipient.
 * - Validator-runtime knobs (`validator_model_id`, `validator_web_*`)
 *   stay inside **Validator** rather than splitting across Validator +
 *   Runtime, so an operator sees "all validator settings together".
 * - Curator-runtime knobs (`model_id`, `web_*`) stay in **Runtime**
 *   because the curator predates the prefix convention; that asymmetry
 *   is already documented in the validator's `agent-config.ts`.
 */
export const AGENT_CONFIG_METADATA: Readonly<Record<string, AgentConfigMetadata>> = {
  // ─── Curator (policy / catalogue tuning, non-runtime) ───────────────
  categories: {
    label: "Categories",
    description:
      "Comma-separated list of categories the curator scopes its discovery to. New entries widen the search; removing one stops new drafts in that bucket but does not retroactively prune existing rows.",
    section: "curator",
    inputKind: "text",
  },
  monitored_sources: {
    label: "Monitored sources",
    description:
      "Comma-separated list of upstream sources the curator considers authoritative for discovery (e.g. g2, capterra, product_hunt).",
    section: "curator",
    inputKind: "text",
  },
  min_new_drafts_per_run: {
    label: "Min new drafts per run",
    description:
      "Lower bound on draft rows the curator must propose per run. A run that lands below this is treated as a soft warning in the run summary.",
    section: "curator",
    inputKind: "text",
  },
  max_new_drafts_per_run: {
    label: "Max new drafts per run",
    description:
      "Hard cap on draft rows written per run. Protects the HITL queue from being flooded by a single curator pass.",
    section: "curator",
    inputKind: "text",
  },
  min_problem_tags: {
    label: "Min problem tags",
    description:
      "Lower bound on the count of `problem_tags` per draft. Curator drafts below the floor fail the structural gate.",
    section: "curator",
    inputKind: "text",
  },
  max_problem_tags: {
    label: "Max problem tags",
    description:
      "Upper bound on the count of `problem_tags` per draft. Stops the model padding tags to game the structural gate.",
    section: "curator",
    inputKind: "text",
  },
  min_embedding_text_chars: {
    label: "Min embedding text chars",
    description:
      "Minimum character count for `embedding_text` per draft. Below this, the row is rejected as too thin to embed usefully.",
    section: "curator",
    inputKind: "text",
  },
  quality_score_threshold: {
    label: "Quality score threshold",
    description:
      "Curator's pre-validator quality floor. Drafts scoring below this never reach `tools_draft`. Distinct from `validator_quality_threshold` which the validator applies post-curator.",
    section: "curator",
    inputKind: "text",
  },
  excluded_tool_ids: {
    label: "Excluded tool IDs",
    description:
      "Comma-separated allowlist of tool IDs the curator must skip — e.g. tools the team has explicitly de-listed. Empty by default.",
    section: "curator",
    inputKind: "text",
  },
  confidence_score_minimum: {
    label: "Confidence score minimum",
    description:
      "Pre-gate floor for the curator's 8-signal `confidence_score`. Any draft below this is auto-rejected before the 12-check structural gate runs. Range 0.0–1.0.",
    section: "curator",
    inputKind: "text",
  },

  // ─── Validator (all `validator_*` keys) ─────────────────────────────
  validator_max_rows_per_run: {
    label: "Max rows per run",
    description:
      "Hard cap on `tools_draft` rows the validator scores per run. Enforced before the LLM sees the rows so a forgetful prompt cannot blow through the budget.",
    section: "validator",
    inputKind: "text",
  },
  validator_quality_threshold: {
    label: "Quality threshold",
    description:
      "Quality-score threshold below which `validator_flag = TRUE`. On the prompt's 1.0–5.0 scale.",
    section: "validator",
    inputKind: "text",
  },
  validator_max_run_time_minutes: {
    label: "Max run time (minutes)",
    description:
      "Wall-clock cap for a validator run. Independent from the orchestrator's per-attempt dispatch timeout.",
    section: "validator",
    inputKind: "text",
  },
  validator_model_id: {
    label: "Model ID",
    description:
      "Anthropic model id for validator runs. Switch between Haiku and Sonnet without a redeploy.",
    section: "validator",
    inputKind: "select",
  },
  validator_web_search_max_uses: {
    label: "Web search max uses",
    description:
      "Per-run cap on the validator's Anthropic web_search tool calls. `0` disables the tool entirely (the validator omits it from the registered tool list so the model never sees it).",
    section: "validator",
    inputKind: "text",
  },
  validator_web_fetch_max_uses: {
    label: "Web fetch max uses",
    description:
      "Per-run cap on the validator's Anthropic web_fetch tool calls. `0` disables the tool entirely.",
    section: "validator",
    inputKind: "text",
  },

  // ─── Catalogue (knobs acting on published tools_and_services) ───────
  stale_threshold_days: {
    label: "Stale threshold (days)",
    description:
      "How many days after `last_updated` a catalogue row is considered stale. Drives the Catalogue page's stale / stale-soon badges.",
    section: "catalogue",
    inputKind: "text",
  },

  // ─── Runtime (curator runtime + global cron knobs) ──────────────────
  run_frequency: {
    label: "Run frequency (cron)",
    description:
      "Cron expression for scheduled curator runs. Honoured once the M3 scheduler lands; ignored under M1 manual dispatch.",
    section: "runtime",
    inputKind: "text",
  },
  run_timezone: {
    label: "Run timezone",
    description:
      "IANA timezone the cron expression is evaluated against (e.g. `Europe/London`).",
    section: "runtime",
    inputKind: "text",
  },
  max_run_time_minutes: {
    label: "Max run time (minutes)",
    description:
      "Wall-clock cap for a curator run. Independent from the orchestrator's per-attempt dispatch timeout.",
    section: "runtime",
    inputKind: "text",
  },
  model_id: {
    label: "Model ID",
    description:
      "Anthropic model id for curator runs. Switch between Haiku (cheaper, higher rate ceilings) and Sonnet/Opus (better instruction-following) without a redeploy.",
    section: "runtime",
    inputKind: "select",
  },
  web_search_max_uses: {
    label: "Web search max uses",
    description:
      "Per-run cap on the curator's Anthropic web_search tool calls. `0` disables the tool entirely. Caps exist primarily to keep input-token accumulation under the operator's API tier rate limit.",
    section: "runtime",
    inputKind: "text",
  },
  web_fetch_max_uses: {
    label: "Web fetch max uses",
    description:
      "Per-run cap on the curator's Anthropic web_fetch tool calls. `0` disables the tool entirely.",
    section: "runtime",
    inputKind: "text",
  },

  // ─── Cost ───────────────────────────────────────────────────────────
  max_cost_per_run: {
    label: "Max cost per run (USD)",
    description:
      "Per-run LLM spend (USD) above which the orchestrator emails a cost alert to `notify_email`. `0` disables the alert. Alerts fire once per breaching run on the terminal-state hook.",
    section: "cost",
    inputKind: "text",
  },

  // ─── Notifications ──────────────────────────────────────────────────
  notify_email: {
    label: "Notify email",
    description:
      "Comma-separated admin recipients for both the HITL `review_ready` / reminder emails and the cost-alert email. Sender domain must be Resend-verified before mail leaves the platform.",
    section: "notifications",
    inputKind: "email-list",
  },
  draft_review_deadline_hours: {
    label: "Draft review deadline (hours)",
    description:
      "Hours from `pending_review` entry until the HITL deadline. Drives the review-batch countdown and the reminder sweeper.",
    section: "notifications",
    inputKind: "text",
  },

  // ─── Public chat (abuse / cost defence for the M2 chatbot) ──────────
  public_max_prompts_per_session: {
    label: "Max prompts per session",
    description:
      "Hard cap on prompts a public-chat visitor may send within one session. Once reached, the orchestrator responds `session_quota_reached` and the chatbot widget ends the conversation. Counter is `client_sessions.prompts_used`; increment is atomic on send-attempt so concurrent requests cannot both pass at cap-1.",
    section: "public-chat",
    inputKind: "text",
  },
  public_max_prompt_chars: {
    label: "Max prompt length (characters)",
    description:
      "Per-prompt character cap on the chatbot's send-message body. Oversize messages return `prompt_too_long` with the cap echoed back as `max_chars`. Tune to balance question depth against prompt-injection payload size.",
    section: "public-chat",
    inputKind: "text",
  },
  public_rate_limit_per_ip_per_min: {
    label: "Rate limit (requests / IP / min)",
    description:
      "Per-IP token-bucket cap for the public chat surface. The bucket refills at `cap` tokens per minute up to `cap` burst tokens; an over-limit request returns `rate_limited` with `retry_after_seconds`. Distinct from the auth-redeem brute-force limiter (5/15 min lockout).",
    section: "public-chat",
    inputKind: "text",
  },

  // ─── Analyst (all `analyst_*` keys — M2 issue #142) ────────────────
  // Kept in **Runtime** rather than a new "Analyst" section because the
  // admin-ui `/settings` panel groups by `AgentConfigSection` and adding
  // a new section forces SPA changes that are out of scope for #142.
  // Mirrors how the curator's runtime knobs sit under `"runtime"` rather
  // than a `"curator-runtime"` split — the agent name is already encoded
  // in the key prefix.
  analyst_model_id: {
    label: "Analyst model ID",
    description:
      "Anthropic model id for Analyst runs. Sonnet 4.6 by default — the Analyst's rubric reasoning (objective-tool matching + combination cohesion) sits above what Haiku reliably handles. Switch without a redeploy.",
    section: "runtime",
    inputKind: "select",
  },
  analyst_max_run_time_minutes: {
    label: "Analyst max run time (minutes)",
    description:
      "Wall-clock cap for an Analyst run. Bounds the LLM loop's three sequential phases (scoring, combinations, optional custom proposal) before the runner's abort signal fires. Independent from the orchestrator's per-attempt dispatch timeout.",
    section: "runtime",
    inputKind: "text",
  },
  analyst_objective_threshold: {
    label: "Analyst objective threshold",
    description:
      "The 40% gate (m2-3-9). When the best single-tool or combination union-of-objectives coverage is below this fraction (0.0–1.0), the Analyst routes to the custom-proposal path instead of writing a tool-assembly recommendation.",
    section: "runtime",
    inputKind: "text",
  },
  analyst_max_candidates_for_combinations: {
    label: "Analyst max candidates for combinations",
    description:
      "Top-K cap on single-tool scorers fed into the combination phase. Combinations are enumerated only among the top-K (`C(K,2) + C(K,3)`), capping LLM cost on large catalogues. Default 8 → 84 combinations enumerated pre-shortlist.",
    section: "runtime",
    inputKind: "text",
  },
  analyst_max_combinations_to_judge: {
    label: "Analyst max combinations to judge",
    description:
      "Cap on LLM-judged integration-compatibility verdicts per run. Combinations are sorted by union-of-objectives coverage and only the top N are sent for cohesion judgement; the rest fall back to their set-math coverage only.",
    section: "runtime",
    inputKind: "text",
  },

  // ─── Match Validator (all `match_validator_*` keys — M2 issue #143) ──
  // Same posture as the Analyst block above: kept under "runtime" so the
  // admin-ui settings panel doesn't need new section wiring.
  match_validator_model_id: {
    label: "Match Validator model ID",
    description:
      "Anthropic model id for Match Validator LLM-judged checks (MV3 problem relevance, MV4 rationale accuracy, list-level ranking order). Opus 4.7 by default — judging the Analyst's reasoning honestly is the load-bearing capability, so the tier sits at the platform's strongest.",
    section: "runtime",
    inputKind: "select",
  },
  match_validator_max_run_time_minutes: {
    label: "Match Validator max run time (minutes)",
    description:
      "Wall-clock cap for a Match Validator run. The runner makes one tool-call LLM round-trip over all recommendations; the cap is generous forward-compat for future per-rec calls.",
    section: "runtime",
    inputKind: "text",
  },
  match_validator_score_pass_threshold: {
    label: "Match Validator score pass threshold (0–5)",
    description:
      "Gate threshold for the m2-7-8 metric — the average per-rec `match_validation_score` across kept recommendations must clear this value on a curated test set. Surfaced in `agent_config` so the snapshot carries the gate value the live-test asserted against.",
    section: "runtime",
    inputKind: "text",
  },

  // ─── Intake Validator (all `intake_validator_*` keys — M2 issue #141) ─
  // Same posture as the Analyst / Match Validator blocks above: kept
  // under "runtime" so the admin-ui settings panel doesn't need new
  // section wiring.
  intake_validator_model_id: {
    label: "Intake Validator model ID",
    description:
      "Anthropic model id for Intake Validator LLM-judged checks (completeness, clarity, internal consistency on a confirmed problem memo). Haiku 4.5 by default — the per-engagement call count is at most 1 and the checks sit comfortably within Haiku's level; staying off the shared Sonnet/Opus rate budget matters at M2.",
    section: "runtime",
    inputKind: "select",
  },
  intake_validator_max_run_time_minutes: {
    label: "Intake Validator max run time (minutes)",
    description:
      "Wall-clock cap on the Intake Validator's LLM loop. The single call typically completes in ≤30 s; the cap is generous forward-compat headroom. Operator-supplied values above `MAX_RUN_TIME_MINUTES_CEILING` (55) are silently clamped at parse time so the runner's wall-clock signal fires before Cloud Run's 60-min request timeout cuts the blocking-SSE dispatch.",
    section: "runtime",
    inputKind: "text",
  },
};
